import { useState } from 'react';
import '../../styles/onboardingFlow/resetPassword/resetPassword.scss';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import MainSquareButton from '../../ui/buttons/MainSquareButton';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import useTitle from '../../customHooks/useTitle';
import AINormalInput from '../../ui/inputs/AINormalInput';
import { applicationWWWXRLEncodedBodyBuilder } from '../../lib/utils/body-builder';
import { PASSWORD_CHANGE_ENDPOINT } from '../../lib/constants/central-api';
import { RequestInfo } from '../../interfaces/RequestInfo';
import { asyncHandler } from '../../lib/api/async-handler';
import { AnimatePresence } from 'framer-motion';
import adupLogoSet from '../../assets/onboarding/common/adup-logo-set.png';
import PasswordValidationView from './PasswordValidationView';
import isNotEmptyString from '../../lib/helper-functions/isNotEmptyString';
import OnboardingMobileLogoPanel from '../onboardingComponents/OnboardingMobileLogoPanel';
import OnboardingBackButton from '../onboardingComponents/OnboardingBackButton';

interface ResetPasswordState {
  token: string;
}

const ResetPassword = () => {
  const { t } = useTranslation();
  useTitle(t('ResetPasswordMainText'));
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as ResetPasswordState | undefined;
  const token = state?.token;

  const userEmail = useSelector((state: RootState) => state.user.profile.email);

  const [password, setPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isPasswordInputFocused, setIsPasswordInputFocused] = useState(false);

  const [hasUppercase, setHasUppercase] = useState<boolean>(false);
  const [hasLowercase, setHasLowercase] = useState<boolean>(false);
  const [hasNumber, setHasNumber] = useState<boolean>(false);
  const [hasSpecialChar, setHasSpecialChar] = useState<boolean>(false);
  const [isLongEnough, setIsLongEnough] = useState<boolean>(false);

  const validatePassword = (password: string) => {
    setHasUppercase(/[A-Z]/.test(password));
    setHasLowercase(/[a-z]/.test(password));
    setHasNumber(/[0-9]/.test(password));
    setHasSpecialChar(/[^a-zA-Z0-9]/.test(password));
    setIsLongEnough(password.length >= 8);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newPassword = event.target.value;
    setPassword(newPassword);
    validatePassword(newPassword);
  };

  const showPasswordValidationView =
    !hasUppercase ||
    !hasLowercase ||
    !hasNumber ||
    !hasSpecialChar ||
    !isLongEnough;

  const isDisabled =
    !hasUppercase ||
    !hasLowercase ||
    !hasNumber ||
    !hasSpecialChar ||
    !isLongEnough ||
    password !== confirmPassword ||
    !isNotEmptyString(token) ||
    !isNotEmptyString(userEmail);

  async function onSubmit(e: any) {
    e.preventDefault();
    if (!token || token === '') return;

    const sendingData = {
      email: userEmail,
      password,
      password_confirmation: confirmPassword,
      token: token,
    };
    try {
      const requestInfo: RequestInfo = {
        url: PASSWORD_CHANGE_ENDPOINT,
        method: 'POST',
        body: applicationWWWXRLEncodedBodyBuilder(sendingData),
        headers: {
          Accept: 'application/json',
          'Content-type': 'application/x-www-form-urlencoded',
        },
      };

      const actionType = 'PASSWORD_CHANGE';
      const customMsg = 'Password Changed Successfully!';

      const res: any = await asyncHandler(requestInfo, actionType, customMsg);

      const response = res.data;
      if (res.actionType === actionType && response.success === true) {
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      }
    } catch (exception) {
      console.log('Error during authentication request');
    }
  }

  return (
    <div className='onboarding-reset-password'>
      {/* Logo panel (mobile only) */}
      <OnboardingMobileLogoPanel />

      <div className='onboarding-reset-password__container'>
        {/* Back button (mobile only) */}
        <OnboardingBackButton
          onClick={() => navigate(-1)}
          aria-label={t('GoBack')}
        />

        {/* Logo (desktop only; hidden on mobile when logo panel is shown) */}
        <div className='onboarding-reset-password__logo'>
          <img src={adupLogoSet} alt='AdUp Logo' />
        </div>

        {/* Heading */}
        <div className='onboarding-reset-password__header'>
          <h1>{t('ResetPasswordMainText')}</h1>
          <p>{t('ResetPasswordSubText-new-ui')}</p>
        </div>

        {/* Form */}
        <form onSubmit={onSubmit} className='onboarding-reset-password__form'>
          {/* Password Input */}
          <div className='onboarding-reset-password__field onboarding-reset-password__field--password'>
            <AINormalInput
              id='reset-password-form-password'
              value={password}
              changeListeners={[(e) => handleChange(e)]}
              placeholder={t('Password')}
              type='password'
              innerPlaceholder={t('EnterYourNewPassword')}
              required={true}
              focusListeners={[
                (e) => {
                  setIsPasswordInputFocused(true);
                },
              ]}
              blurListeners={[
                (e) => {
                  setIsPasswordInputFocused(false);
                },
              ]}
            />
            {/* Password Validation - Floating */}
            <AnimatePresence exitBeforeEnter>
              {isPasswordInputFocused && showPasswordValidationView && (
                <PasswordValidationView
                  hasUppercase={hasUppercase}
                  hasLowercase={hasLowercase}
                  hasNumber={hasNumber}
                  hasSpecialChar={hasSpecialChar}
                  isLongEnough={isLongEnough}
                />
              )}
            </AnimatePresence>
          </div>

          {/* Confirm Password Input */}
          <div className='onboarding-reset-password__field'>
            <AINormalInput
              id='reset-password-form-confirm-password'
              value={confirmPassword}
              type='password'
              changeListeners={[(e) => setConfirmPassword(e.target.value)]}
              placeholder={t('ConfirmPassword')}
              innerPlaceholder={t('ConfirmYourNewPassword')}
              required={true}
            />
          </div>

          {/* Reset Password Button */}
          <div
            className='onboarding-reset-password__button'
            title={isDisabled ? t('ConfirmPasswordDoNotMatch') : ''}
          >
            <MainSquareButton
              value={t('ChangePassword')}
              type='submit'
              disabled={isDisabled}
              classes='onboarding-reset-password__continue-btn'
            />
          </div>
        </form>
      </div>

      {/* Copyright */}
      <div className='onboarding-reset-password__copyright'>
        <span>© AdUp {new Date().getFullYear()}</span>
      </div>
    </div>
  );
};

export default ResetPassword;
